<!-- CityAreaSelection.svelte -->

<script>
  import { onMount, createEventDispatcher } from 'svelte';
  import { Capacitor } from '@capacitor/core';
  import { App } from '@capacitor/app';

  let cities = [];
  let deliveryPoints = [];
  let selectedCity = null;
  let selectedDeliveryPoint = null;
  let isLoadingCities = true;
  let isLoadingDeliveryPoints = false;
  let loadingMessage = 'Loading available cities...';
  let error = null;
  let retryCount = 0;
  const maxRetries = 3;

  const dispatch = createEventDispatcher();

  onMount(async () => {
    await fetchCities();

    if (Capacitor.isNativePlatform()) {
      App.addListener('backButton', goBack);
    }
  });

  function goBack() {
    if (selectedCity) { // Modified condition here
      // Go back to city selection
      selectedCity = null;
      deliveryPoints = [];
    } else {
      // Close the component
      dispatch('close');
    }
  }

  async function fetchCities() {
    isLoadingCities = true;
    error = null;

    try {
      const accessToken = localStorage.getItem('accessToken');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch('https://groupfruits.online/shop/api/ActiveCityListView/', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${accessToken}`,
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        cities = await response.json();
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        error = 'Request timed out. Please check your internet connection.';
      } else if (err instanceof TypeError && err.message === 'Failed to fetch') {
        error = 'Network error. Please check your internet connection.';
      } else {
        error = `Error fetching cities: ${err.message}`;
      }

      if (retryCount < maxRetries) {
        retryCount++;
        loadingMessage = `Retrying... (Attempt ${retryCount}/${maxRetries})`;
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retrying
        await fetchCities();
      }
    } finally {
      isLoadingCities = false;
    }
  }

  async function fetchDeliveryPoints(cityId) {
    isLoadingDeliveryPoints = true;
    loadingMessage = 'Loading pickup points...';
    error = null;
    retryCount = 0;

    try {
      const accessToken = localStorage.getItem('accessToken');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      const response = await fetch('https://groupfruits.online/shop/api/DeliveryPointsForCityView/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${accessToken}`,
        },
        body: JSON.stringify({ city_id: cityId }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        deliveryPoints = data.delivery_points;
        loadingMessage = 'Loading images...';
        await preloadImages(deliveryPoints);
      } else {
        if (response.status === 404) {
          deliveryPoints = []; // Handle 404 as no delivery points, not an error
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        error = 'Request timed out. Please check your internet connection.';
      } else if (err instanceof TypeError && err.message === 'Failed to fetch') {
        error = 'Network error. Please check your internet connection.';
      } else {
        error = `Error fetching delivery points: ${err.message}`;
      }

      if (retryCount < maxRetries) {
        retryCount++;
        loadingMessage = `Retrying... (Attempt ${retryCount}/${maxRetries})`;
        await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds before retrying
        await fetchDeliveryPoints(cityId);
      }
    } finally {
      isLoadingDeliveryPoints = false;
    }
  }

  function preloadImages(deliveryPoints) {
    const imagePromises = deliveryPoints.map(point => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = reject;
        img.src = point.main_image ? point.main_image.image : 'https://via.placeholder.com/80x80?text=No+Image';
      });
    });

    return Promise.all(imagePromises);
  }

  function handleCitySelection(city) {
    selectedCity = city;
    fetchDeliveryPoints(city.id);
  }

  let selectedDeliveryPointTimeout;

  function handleDeliveryPointClick(point) {
    selectedDeliveryPoint = point.id;
    deliveryPoints = deliveryPoints.map(dp => ({ ...dp, selected: false, highlighted: false }));

    const selectedDeliveryPointObj = deliveryPoints.find(dp => dp.id === selectedDeliveryPoint);
    selectedDeliveryPointObj.selected = true;
    selectedDeliveryPointObj.highlighted = true;

    clearTimeout(selectedDeliveryPointTimeout);
    selectedDeliveryPointTimeout = setTimeout(() => {
      selectedDeliveryPointObj.highlighted = false;
      dispatch('deliveryPointSelected', { deliveryPointId: selectedDeliveryPoint, cityId: selectedCity.id });
      selectedDeliveryPoint = null;
    }, 100);
  }
</script>

<style>
  :global(body) {
    -webkit-tap-highlight-color: transparent;
    margin: 0;
    padding: 0;
    font-family: 'Roboto', sans-serif;
    background-color: #f5f5f5;
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
    padding-top: calc(1rem + env(safe-area-inset-top));
    padding-bottom: calc(4rem + env(safe-area-inset-bottom));
    max-width: 37.5rem;
    margin: 0 auto;
  }

  .delivery-point {
    background-color: #ffffff;
    border-radius: 0.75rem;
    padding: 1rem;
    cursor: pointer;
    transition: box-shadow 0.3s ease, transform 0.3s ease, background-color 0.3s ease;
    box-sizing: border-box;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
    user-select: none;
    position: relative;
    display: flex;
    align-items: center;
  }

  .delivery-point:active {
    transform: scale(0.98);
  }

  .delivery-point:hover {
    box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
  }

  .delivery-point.selected,
  .delivery-point.highlighted {
    background-color: #e0f7e0;
    box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
  }

  .delivery-point.selected::after,
  .delivery-point.highlighted::after {
    content: '✓';
    position: absolute;
    top: 0.5rem;
    right: 0.75rem;
    font-size: 1.25rem;
    color: #4caf50;
  }

  .delivery-point.selected {
    animation: select-animation 0.5s ease-in-out;
  }

  .delivery-point.selected::after {
    animation: check-animation 0.5s ease-in-out;
  }

  .delivery-point-image {
    width: 5rem;
    height: 5rem;
    border-radius: 0.5rem;
    object-fit: cover;
    margin-right: 1rem;
  }

  .delivery-point-details {
    flex: 1;
  }

  .delivery-point-timings {
    font-size: 0.875rem;
    color: #999;
  }

  .delivery-point h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: #333333;
  }

  .delivery-point p {
    margin: 0;
    font-size: 0.875rem;
    color: #666666;
    white-space: pre-wrap;
  }

  .header {
    display: flex;
    padding: 1.25rem;
    padding-top: calc(1.25rem + env(safe-area-inset-top));
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 1;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
  }

  .back-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #555;
    padding: 0;
    margin-right: 0.5rem;
    display: flex;
    align-items: center;
  }

  .header-content {
    display: flex;
    flex-grow: 1;
    justify-content: center;
    align-items: center;
  }

  .header h2 {
    font-size: 1.25rem;
    margin: 0;
    color: #000;
  }

  .loader {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }

  .dot-loader {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .dot {
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    background-color: #3498db;
    margin: 0 0.375rem;
    animation: dot-pulse 1.4s cubic-bezier(0.455, 0.03, 0.515, 0.955) infinite;
  }

  .dot:nth-child(1) {
    animation-delay: -0.32s;
  }

  .dot:nth-child(2) {
    animation-delay: -0.16s;
  }

  .city-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-top: 1rem;
  }

  .city-card {
    background-color: white;
    border-radius: 0.75rem;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
    overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    cursor: pointer;
    height: 6rem;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .city-card:active {
    transform: scale(0.98);
  }

  .city-card:hover {
    box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.15);
  }

  .city-name {
    font-size: 1.25rem;
    font-weight: 500;
    text-align: center;
    color: #333;
    z-index: 1;
    padding: 0 1rem;
  }

  .city-background {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(240, 240, 240, 0.5);
    background-image: linear-gradient(45deg, rgba(255, 255, 255, 0.2) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.2) 75%, transparent 75%, transparent);
    background-size: 8px 8px;
    border-radius: 0.75rem;
  }

  .retry-button {
    background-color: #3498db;
    color: white;
    border: none;
    border-radius: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.3s ease;
    margin-top: 1rem;
  }

  .retry-button:hover {
    background-color: #2980b9;
  }

  .error-container {
    text-align: center;
    padding: 2rem;
  }

  .error-message {
    color: #e74c3c;
    font-weight: 500;
    margin-bottom: 1rem;
  }

  @keyframes dot-pulse {
    0%, 80%, 100% {
      transform: scale(0);
      opacity: 0;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes select-animation {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes check-animation {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
</style>

<svelte:head>
  <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap" rel="stylesheet">
</svelte:head>

{#if isLoadingCities || isLoadingDeliveryPoints}
  <div class="loader">
    <div class="dot-loader">
      <div class="dot"></div>
      <div class="dot"></div>
      <div class="dot"></div>
    </div>
    <p style="margin-top: 24px; color: #3498db; font-weight: 500;">{loadingMessage}</p>
  </div>
{:else if error}
  <div class="error-container">
    <p class="error-message">{error}</p>
    <button class="retry-button" on:click={() => selectedCity ? fetchDeliveryPoints(selectedCity.id) : fetchCities()}>
      Retry
    </button>
  </div>

{:else if !selectedCity}
  <!-- City Selection View -->
  <div class="header">
    <div class="header-content">
      <h2>Select Your City</h2>
    </div>
  </div>

  <div class="container">
    {#if cities.length > 0}
      <div class="city-grid">
        {#each cities as city}
          <div class="city-card" on:click={() => handleCitySelection(city)}>
            <div class="city-background"></div>
            <h3 class="city-name">{city.name}</h3>
          </div>
        {/each}
      </div>
    {:else}
      <p>No cities available at the moment. Please try again later.</p>
    {/if}
  </div>
{:else if deliveryPoints.length > 0}
  <!-- Delivery Points View -->
  <div class="header">
    <button class="back-button" on:click={goBack}>
      ←
    </button>
    <div class="header-content">
      <h2>Select Pickup Point in {selectedCity.name}</h2>
    </div>
  </div>

  <div class="container">
    {#each deliveryPoints as point}
      <div
        class="delivery-point"
        class:selected={selectedDeliveryPoint === point.id}
        class:highlighted={point.highlighted}
        on:click={() => handleDeliveryPointClick(point)}
      >
        <img
          class="delivery-point-image"
          src={point.main_image ? point.main_image.image : 'https://via.placeholder.com/80x80?text=No+Image'}
          alt={point.name}
        />
        <div class="delivery-point-details">
          <h3>{point.area_label}</h3>
          <p><strong>{point.name}</strong></p>
          <p>{point.address.replace(/\\r\\n/g, ', ')}</p>
          {#if point.timings}
            <p class="delivery-point-timings">Shop Timings: {point.timings}</p>
          {/if}
        </div>
      </div>
    {/each}
  </div>
{:else}
  <!-- No Delivery Points Available -->
  <div class="header">
    <button class="back-button" on:click={goBack}>
      ←
    </button>
    <div class="header-content">
      <h2>{selectedCity.name}</h2>
    </div>
  </div>

  <div class="container">
    <div style="text-align: center; padding: 2rem;">
      <p style="font-size: 1.125rem; color: #666;">We are launching shortly in {selectedCity.name}.</p>
      <p style="margin-top: 1rem;">Please check back later or select another city.</p>
      <button
        class="retry-button"
        style="margin-top: 1.5rem; background-color: #4caf50;"
        on:click={goBack}
      >
        Choose Another City
      </button>
    </div>
  </div>
{/if}